import React, { useState, useEffect, useRef } from "react";
import _ from "lodash";
import { showNotification } from "@mantine/notifications";

import {
  Button,
  Table,
  Group,
  Text,
  Chip,
  Space,
  Title,
  Grid,
  Card,
  Divider,
  Tabs,
  Badge,
  LoadingOverlay,
  Menu,
  Switch,
  Box,
  Stack,
  Center,
  ActionIcon,
  Checkbox,
} from "@mantine/core";
import { useCellRender } from "../hooks/useCellRender";

import {
  IconFile,
  IconRefresh,
  IconTrash,
  IconPrinter,
  IconAlertCircle,
  IconAlertTriangle,
  IconDotsVertical,
} from "@tabler/icons";
import { Accordion, useMantineTheme } from "@mantine/core";
import { useFormatter } from "../hooks/useFomatter";
import { useServerApi } from "../hooks/userServerApi";
import { useForm } from "@mantine/form";
import useDeepCompareEffect from "../hooks/useDeepCompareEffect";
import ReactJson from "react-json-view";
import moment from "moment";
import RemoteSelect2 from "./remoteSelect2";
import { useForceUpdate } from "@mantine/hooks";
import GISettlementCrossCheckTab from "./giSettlementCrossCheckTab";
import EntityTabAnchor from "../pages/account/payroll2/EntityTabAnchor";
import { openFileUrl } from "../utils/openReport";

const showPolicyStatus =
  process.env.REACT_APP_GISETTLEMENT_SHOW_POLICY_STATUS === "true";

const RowHeader = ({
  settlement,
  status,
  mainStatus,
  onRemoveSettlement,
  onResetSettlement,
  onConfirmSettlement,
  onUnConfirmSettlement,
}) => {
  const [cellRender] = useCellRender();
  const showUnConfirm = mainStatus !== "COMPLETED";
  const { debitNotes } = settlement;

  let numOfAmountDifference = 0;
  for (const dn of debitNotes) {
    if (dn.payment?.amount - dn.clientPayable > 0.01) {
      numOfAmountDifference++;
    }
  }

  return (
    <>
      {settlement && (
        <>
          <Grid justify="space-between">
            <Grid.Col span={5}>
              <>
                {settlement && (
                  <div>{cellRender.ProductInfo(settlement.provider)}</div>
                )}
              </>
            </Grid.Col>
            <Grid.Col span={3}>
              <>
                {settlement.debitNotes && (
                  <>
                    <Title order={3}>{settlement.debitNotes.length}</Title>
                    <Text size={"xs"} color="dimmed">
                      Total DebitNotes
                    </Text>
                  </>
                )}
              </>
            </Grid.Col>
            <Grid.Col span={showUnConfirm ? 3 : 4}>
              <div>
                <Title order={3}>
                  {cellRender.Currency(settlement.totalAmount)}
                </Title>

                <Text size={"xs"} color="dimmed">
                  Total Settle Amount
                </Text>
              </div>
            </Grid.Col>
            <Grid.Col span={showUnConfirm ? 1 : 0}>
              {onUnConfirmSettlement && showUnConfirm && (
                <Button
                  onClick={(e) => {
                    console.log("onUnConfirmSettlement", settlement.provider);
                    onUnConfirmSettlement(settlement.provider?._id);
                    e.stopPropagation();
                  }}
                  size="xs"
                  variant="default"
                  mb={"xs"}
                >
                  UnConfirm
                </Button>
              )}

              {onConfirmSettlement && (
                <Button
                  onClick={(e) => {
                    onConfirmSettlement(settlement.provider?._id);
                    e.stopPropagation();
                  }}
                  size="xs"
                  variant="default"
                  mb={"xs"}
                >
                  Confirm
                </Button>
              )}
              {onRemoveSettlement && (
                <Button
                  onClick={(e) => {
                    onRemoveSettlement(settlement.provider?._id);
                    e.stopPropagation();
                  }}
                  size="xs"
                  variant="default"
                  mb={"xs"}
                  c="red"
                >
                  Remove
                </Button>
              )}
            </Grid.Col>
          </Grid>

          {numOfAmountDifference > 0 && (
            <Group>
              <IconAlertTriangle size={16} color="red" />
              <Text color="red">
                {numOfAmountDifference} Debit Notes have different payment
                amount from Client Payable
              </Text>
            </Group>
          )}
        </>
      )}
    </>
  );
};

const RowContent = ({ settlement, onRemoveDebitNote }) => {
  const { debitNotes } = settlement;
  const [cellRender] = useCellRender();
  const [api] = useServerApi();
  const [selectedDNs, setSelectedDNs] = useState([]);

  let numOfAmountDifference = 0;

  const handleRemoveDNs = async (dnIds) => {
    const ans = window.confirm(
      `Are you sure to remove ${dnIds.length} debitNote from this settlement?`
    );
    if (!ans) return;
    if (!onRemoveDebitNote) return;
    if (selectedDNs.length === 0) return;

    onRemoveDebitNote(selectedDNs);
    setSelectedDNs([]);
  };

  const handleRemoveDN = async (dn) => {
    const ans = window.confirm(
      `Are you sure to remove the debitNote ${dn.code} from this settlement?`
    );
    if (!ans) return;
    if (!onRemoveDebitNote) return;
    onRemoveDebitNote([dn._id]);
  };

  return (
    <>
      <Divider
        mt={"xl"}
        mb={"xl"}
        my="xs"
        label="Debit Notes"
        labelPosition="center"
        variant="dashed"
      />
      <Group my="md">
        <Text size="xs">Selected</Text>
        <Badge size="xs" radius="xs">
          {selectedDNs.length}
        </Badge>

        <Menu shadow="md">
          <Menu.Target>
            <ActionIcon
              disabled={selectedDNs.length === 0}
              variant="light"
              size="sm"
            >
              <IconDotsVertical size={16} />
            </ActionIcon>
          </Menu.Target>

          <Menu.Dropdown>
            <Menu.Item
              onClick={async () => {
                const removed = await handleRemoveDNs(selectedDNs);
                if (removed) {
                  setSelectedDNs([]);
                }
              }}
            >
              <Text size="xs">Remove Selected</Text>
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Group>

      <Table fontSize={"sm"} striped highlightOnHover>
        <thead>
          <tr>
            <th>
              <Checkbox
                size="xs"
                checked={selectedDNs.length === debitNotes.length}
                onChange={(e) => {
                  const checked = e.currentTarget.checked;
                  if (checked) {
                    setSelectedDNs(debitNotes.map((b) => b._id));
                  } else {
                    setSelectedDNs([]);
                  }
                }}
              />
            </th>
            <th>
              <Text size="xs">#</Text>
            </th>
            <th>
              <Text size="xs">DN.</Text>
            </th>
            {/* <th>Date</th> */}
            {showPolicyStatus && (
              <th>
                <Text size="xs">Policy Status</Text>
              </th>
            )}
            <th>
              <Text size="xs">Policy No.</Text>
            </th>
            <th>
              <Text size="xs">Product</Text>
            </th>
            <th>
              <Text size="xs">Client Payable</Text>
            </th>
            <th>
              <Text size="xs">Client Payment</Text>
            </th>
            <th>
              <Text size="xs">Net Preimum</Text>
            </th>

            <th></th>
          </tr>
        </thead>
        <tbody>
          {debitNotes &&
            debitNotes.map((n, index) => (
              <>
                <tr key={index}>
                  <td>
                    <Checkbox
                      size="xs"
                      checked={selectedDNs.includes(n._id)}
                      onChange={(e) => {
                        const checked = e.currentTarget.checked;
                        if (checked) {
                          setSelectedDNs([...selectedDNs, n._id]);
                        } else {
                          setSelectedDNs(
                            selectedDNs.filter((id) => id !== n._id)
                          );
                        }
                      }}
                    />
                  </td>
                  <td>{index + 1}</td>
                  <td>
                    <EntityTabAnchor
                      entity="debitNote"
                      id={n._id}
                      label={n.code}
                      size="xs"
                      lineClamp={1}
                    />
                    {n.type === "CREDIT" && (
                      <Badge size="xs" color="orange" mt={2}>
                        CN
                      </Badge>
                    )}
                    <Text size={"xs"} color="dimmed">
                      {moment(n.date).format("LL")}
                    </Text>
                  </td>
                  {showPolicyStatus && (
                    <td>
                      <Badge
                        size="xs"
                        radius={0}
                        color={
                          n.policyStatus === "POLICY_RECEIVED"
                            ? "green"
                            : n.policyStatus === "POLICY_DISPATCHED"
                              ? "blue"
                              : "gray"
                        }
                      >
                        {n.policyStatus || "NONE"}
                      </Badge>
                    </td>
                  )}
                  <td>
                    <Text size={"xs"} lineClamp={1}>
                      {n.policyNumber}
                    </Text>
                  </td>
                  <td>
                    <Text size={"xs"} lineClamp={1}>
                      {n.client?.name}
                    </Text>
                    <Text size={"xs"} color="dimmed" lineClamp={1}>
                      {n.product?.name ?? n.productType?.name}
                    </Text>
                  </td>
                  <td>
                    <Text size="xs">
                      {cellRender.Currency(n.clientPayable)}
                    </Text>
                  </td>

                  <td>
                    <Text
                      size="xs"
                      color={
                        n.payment?.amount - n.clientPayable > 0.01
                          ? "red"
                          : "green"
                      }
                    >
                      {cellRender.Currency(n.payment?.amount)}
                    </Text>
                  </td>
                  <td>
                    <Text
                      size="xs"
                      color={n.costTotal < 0 ? "red" : undefined}
                    >
                      {cellRender.Currency(n.costTotal)}
                    </Text>
                  </td>
                  <td>
                    {onRemoveDebitNote && (
                      <ActionIcon
                        variant="filled"
                        onClick={() => handleRemoveDN(n)}
                      >
                        <IconTrash size={13}></IconTrash>
                      </ActionIcon>
                    )}
                  </td>
                  {/* <td>{cellRender.Currency(b.paidAmount, b.paidCurrency)}</td> */}
                </tr>
              </>
            ))}
        </tbody>
      </Table>
    </>
  );
};

export const SettlementRow = ({
  settlement,
  index,
  status,
  mainStatus,
  onRemoveSettlement,
  onConfirmSettlement,
  onUnConfirmSettlement,
  onRemoveDebitNoteFromSettlement,
  updateSettlementData,
}) => {
  const forceUpdate = useForceUpdate();
  const [api] = useServerApi();
  const handleRemoveDNs = async (removeDNs) => {
    settlement.debitNotes = settlement.debitNotes.filter(
      (dn) => !removeDNs.includes(dn._id)
    );
    for (const dn of removeDNs) {
      await api.GISettlement.removeDebitNote(dn);
    }
    onRemoveDebitNoteFromSettlement(settlement);
    forceUpdate();
  };

  return (
    <>
      {settlement && (
        <Accordion.Item value={"settlement-" + index}>
          <Accordion.Control>
            <RowHeader
              settlement={settlement}
              onRemoveSettlement={onRemoveSettlement}
              onConfirmSettlement={onConfirmSettlement}
              onUnConfirmSettlement={onUnConfirmSettlement}
              status={status}
              mainStatus={mainStatus}
            />
          </Accordion.Control>
          <Accordion.Panel>
            <RowContent
              settlement={settlement}
              onRemoveDebitNote={status === "CONFIRM" ? null : handleRemoveDNs}
            />
          </Accordion.Panel>
        </Accordion.Item>
      )}
    </>
  );
};

const SettlementList = ({
  settlements,
  status,
  form,
  handleConfirmSettlement,
  handleRemoveSettlement,
  handleResetSettlement,
  handleUnConfirmSettlement,
  handleRemoveDebitNoteFromSettlement,
  updateSettlementData,
}) => {
  const forceUpdate = useForceUpdate();

  return (
    <>
      {settlements && (
        <>
          {settlements.length === 0 && (
            <Card>
              <Title order={6}>{`There are no settlements`}</Title>
            </Card>
          )}

          <Accordion variant="separated">
            {settlements.map((s, index) => (
              <>
                <SettlementRow
                  settlement={s}
                  status={status}
                  mainStatus={_.get(form?.values, "status")}
                  index={index}
                  onRemoveSettlement={
                    status === "PENDING"
                      ? () => handleRemoveSettlement(s)
                      : null
                  }
                  onConfirmSettlement={
                    status === "PENDING"
                      ? () => handleConfirmSettlement(s)
                      : null
                  }
                  onUnConfirmSettlement={
                    status === "CONFIRM"
                      ? () => handleUnConfirmSettlement(s)
                      : null
                  }
                  onRemoveDebitNoteFromSettlement={(settlement) =>
                    handleRemoveDebitNoteFromSettlement(settlement)
                  }
                  updateSettlementData={updateSettlementData}
                />
              </>
            ))}
          </Accordion>
        </>
      )}
    </>
  );
};

const MainTab = ({
  settlements,
  form,
  updateSettlementData,
  batchStatus,
  rechecking,
  setRechecking,
}) => {
  const [api] = useServerApi();
  const forceUpdate = useForceUpdate();
  const id = _.get(form?.values, "_id");

  const confirm = settlements?.filter((m) => m.status === "CONFIRM");
  const pending = settlements?.filter((m) => m.status === "PENDING");
  const crossCheckCount = form?.values?.insurerStatement?.rows?.length ?? 0;

  const handleConfirmSettlement = (settlement) => {
    if (!settlement) return;
    settlement.status = "CONFIRM";
    updateSettlementData(settlements);

    forceUpdate();
  };

  const handleUnConfirmSettlement = (settlement) => {
    if (!settlement) return;
    settlement.status = "PENDING";
    updateSettlementData(settlements);
    forceUpdate();
  };

  const handleResetSettlement = async () => {
    if (!settlements) return;
    for (const settlement of settlements) {
      await api.GISettlement.reset(settlement._id);
    }
    form.setFieldValue("settlements", []);
    updateSettlementData([]);
    forceUpdate();
  };

  const handleRemoveSettlement = async (settlement) => {
    try {
      // console.log("handleRemoveSettlement", settlements);
      if (!settlement) return;
      for (const dn of settlement.debitNotes) {
        await api.GISettlement.removeDebitNote(dn._id);
      }

      const newSettlements = settlements?.filter(
        (m) => m.provider?._id !== settlement.provider?._id
      );

      form.setFieldValue("settlements", newSettlements);
      updateSettlementData(newSettlements);
      forceUpdate();
    } catch (error) {
      console.log(error);
    }
  };

  const handleRemoveDebitNoteFromSettlement = async (settlement) => {
    // console.log("handleRemoveDebitNoteFromSettlement > settlement", settlement);
    settlement.numOfDebitNotes = settlement.debitNotes.length;
    settlement.totalAmount = settlement.debitNotes.reduce(
      (acc, m) => acc + m.costTotal,
      0
    );

    updateSettlementData(settlements);
    forceUpdate();
  };

  return (
    <Tabs defaultValue="pending">
      <Tabs.List>
        <Tabs.Tab value="pending">{`Pending (${pending?.length})`}</Tabs.Tab>
        <Tabs.Tab value="confirm">{`Confirm (${confirm?.length})`}</Tabs.Tab>
        <Tabs.Tab value="crossCheck">{`Insurer Statement (${crossCheckCount})`}</Tabs.Tab>
      </Tabs.List>

      <Tabs.Panel value="pending" pt="xl">
        <SettlementList
          settlements={pending}
          status="PENDING"
          form={form}
          handleConfirmSettlement={handleConfirmSettlement}
          handleRemoveSettlement={handleRemoveSettlement}
          handleResetSettlement={handleResetSettlement}
          updateSettlementData={updateSettlementData}
          handleRemoveDebitNoteFromSettlement={
            handleRemoveDebitNoteFromSettlement
          }

          // onChanged={onMatchingChange}
          // status={status}
        />
      </Tabs.Panel>
      <Tabs.Panel value="confirm" pt="xl">
        <SettlementList
          settlements={confirm}
          status="CONFIRM"
          form={form}
          handleUnConfirmSettlement={handleUnConfirmSettlement}
          updateSettlementData={updateSettlementData}
          // onChanged={onMatchingChange}
          // status={status}
        />
      </Tabs.Panel>
      <Tabs.Panel value="crossCheck" pt="xs">
        <GISettlementCrossCheckTab
          form={form}
          status={batchStatus}
          rechecking={rechecking}
          setRechecking={setRechecking}
        />
      </Tabs.Panel>
    </Tabs>
  );
};

const GISettlementTable = ({ form, name }) => {
  const { settlements, _id, status } = form.values;
  const [cellRender] = useCellRender();
  const [formatter] = useFormatter();
  const [exporting, setExporting] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const [exportingDetail, setExportingDetail] = useState(false);
  const [exportingOrderDetail, setExportingOrderDetail] = useState(false);
  const [exportingBankFile, setExportingBankFile] = useState(false);
  const [rechecking, setRechecking] = useState(false);
  const recheckTimerRef = useRef(null);
  const forceUpdate = useForceUpdate();

  const [api] = useServerApi();

  const silentRecheck = async () => {
    const statementRows = form.values?.insurerStatement?.rows;
    if (!_id || status === "COMPLETED" || !statementRows?.length) return;

    setRechecking(true);
    const result = await api.GISettlement.crossCheck(_id);
    setRechecking(false);

    if (result?.success) {
      form.setFieldValue("insurerStatement", result.data.insurerStatement);
      form.setFieldValue(
        "insurerCrossCheckSummary",
        result.data.insurerCrossCheckSummary
      );
      forceUpdate();
    }
  };

  const scheduleSilentRecheck = () => {
    const statementRows = form.values?.insurerStatement?.rows;
    if (!_id || status === "COMPLETED" || !statementRows?.length) return;

    if (recheckTimerRef.current) clearTimeout(recheckTimerRef.current);
    recheckTimerRef.current = setTimeout(silentRecheck, 800);
  };

  useEffect(() => {
    return () => {
      if (recheckTimerRef.current) clearTimeout(recheckTimerRef.current);
    };
  }, []);

  const handleConfirm = async () => {
    const _id = _.get(form.values, "_id");
    if (!_id || exporting) return;

    try {
      await form.save();

      const ans = window.confirm(
        "Are you sure to confirm the settlements? Once confirmed, the Debitnotes will be marked as SETTLED. And bids will be released."
      );
      if (!ans) return;
      setExporting(true);
      await form.save();

      let result = await api.GISettlement.confirm(_id);
      // console.log(result);
      setExporting(false);

      if (result.error) throw result.error;

      form.setFieldValue("status", "COMPLETED");
      form.notifyListRefresh?.();
      setExporting(false);
    } catch (error) {
      showNotification({
        title: "Confirm Fail",
        message: error,
        color: "red",
      });

      setExporting(false);
    }
  };

  const handleExportDetail = async () => {
    if (!_id || exporting) return;
    try {
      await form.save();

      setExporting(true);
      setExportingDetail(true);
      let result = await api.GISettlement.print(_id);
      setExporting(false);
      setExportingDetail(false);

      // console.log(result);
      if (result.url) return await openFileUrl(result.url);
      if (result.error) throw result.error;
    } catch (error) {
      // console.log(error);
      setExporting(false);
      setExportingDetail(false);
    }
  };

  const unsetLoading = () => {
    setLoading(false);
    setExporting(false);
  };

  const updateSettlementData = (settlements) => {
    // console.log("updateSettlementData > settlements", settlements);
    const cofirmedSettlement = settlements?.filter(
      (m) => m.status === "CONFIRM"
    );
    const pendingSettlement = settlements?.filter(
      (m) => m.status === "PENDING"
    );

    const numOfSettlement = settlements?.length;
    const pendingAmount = pendingSettlement.reduce(
      (acc, m) => acc + m.totalAmount,
      0
    );
    const confirmedAmount = cofirmedSettlement.reduce(
      (acc, m) => acc + m.totalAmount,
      0
    );
    const totalAmount = pendingAmount + confirmedAmount;

    // form.setFieldValue("numOfSettlement", numOfSettlement);
    // form.setFieldValue("pendingAmount", pendingAmount);
    // form.setFieldValue("confirmedAmount", confirmedAmount);
    // form.setFieldValue("totalAmount", totalAmount);

    form.updateAndSave({
      numOfSettlement,
      pendingAmount,
      confirmedAmount,
      totalAmount,
      settlements,
    });

    scheduleSilentRecheck();
    forceUpdate();
  };

  const handleGetPendingSettlements = async () => {
    if (!_id || exporting) return;
    try {
      setExporting(true);
      setLoading(true);
      const provider = _.get(form?.values, "provider");
      const settlements = await api.GISettlement.getPending(_id, provider);

      if (_.isEmpty(settlements)) {
        showNotification({
          title: "No pending settlement found",
          message: "No pending settlement found",
          color: "green",
        });
      }

      form.setFieldValue("settlements", settlements);
      updateSettlementData(settlements);
      forceUpdate();

      //Update data
      return unsetLoading();
    } catch (error) {
      //   console.log(error);
      setExporting(false);
      setLoading(false);
    }
  };

  const handleResetSettlement = async () => {
    if (!_id || exporting) return;
    try {
      const ans = window.confirm(
        "Are you sure to reset the settlements? Once reset, all settlements will be released."
      );
      if (!ans) return;

      await api.GISettlement.reset(_id);
      form.setFieldValue("settlements", []);
      updateSettlementData([]);
      forceUpdate();
    } catch (error) {
      showNotification({
        title: "Reset Settlement Fail",
        message: error,
        color: "red",
      });
    }
  };

  const isGetPendingDisable = () => status === "COMPLETED";

  const confirmedCount = settlements?.filter(
    (m) => m.status === "CONFIRM"
  )?.length;

  return (
    <>
      {/* <ReactJson src={form.values} style={{ background: "white" }} collapsed /> */}
      <Group position="apart" mt="xl">
        <Box mb="xl">
          <Text fw={500}>Conditions</Text>
          <Text size={"xs"} c="dimmed">
            1. DebitNotes must have been fully paid ( i.e. paymentStatus is PAID
            )
          </Text>
          <Text size={"xs"} c="dimmed">
            2. DebitNotes must have been confirmed ( i.e. DN Status is CONFIRMED
            )
          </Text>
          <Text size={"xs"} c="dimmed">
            3. DebitNotes must have cost items (costTotal &gt; 0) or be Credit
            Notes (type CN, costTotal &lt; 0)
          </Text>
          {/* <Text size={"xs"} c="dimmed">
            4. DebitNotes commission rate must > 0
          </Text> */}
        </Box>
        <Group position="right" mb={"xl"}>
          {/* {settlements?.length > 0 && (
            <Button
              disabled={
                status === "COMPLETED" || settlements?.length === 0 || exporting
              }
              onClick={() => handleResetSettlement()}
              // leftIcon={<IconRefresh size={14} />}
              color="orange"
            >
              Reset All
            </Button>
          )} */}
          <Button
            disabled={isGetPendingDisable() || exporting}
            loading={isLoading}
            onClick={() => handleGetPendingSettlements()}
            leftIcon={<IconRefresh size={14} />}
          >
            Get Pending Settlements
          </Button>
          <Button
            // color={"green"}
            disabled={exporting}
            loading={exportingDetail}
            onClick={() => handleExportDetail()}
            leftIcon={<IconPrinter size={14} />}
          >
            Print
          </Button>

          <Button
            color={"red"}
            disabled={
              exporting ||
              status === "COMPLETED" ||
              settlements?.length === 0 ||
              confirmedCount === 0
            }
            loading={exportingBankFile}
            onClick={() => handleConfirm()}
            leftIcon={<IconFile size={14} />}
          >
            Confirm
          </Button>
        </Group>
      </Group>
      {/* <ReactJson src={form.values} style={{ background: "white" }} collapsed /> */}
      <MainTab
        settlements={settlements}
        form={form}
        updateSettlementData={updateSettlementData}
        batchStatus={status}
        rechecking={rechecking}
        setRechecking={setRechecking}
      />
    </>
  );
};

export default GISettlementTable;
